import ky, {
  type AfterResponseHook,
  type AfterResponseState,
  type BeforeRequestState,
  type KyInstance,
} from 'ky'
import type { z } from 'zod'

import { env } from '@/shared/config'

import { authSession } from './auth-session'
import { normalizeError } from './normalize-error'


/**
 * The HTTP facade. Only this file imports ky — features call `http.get(...)`
 * and never see a transport type, an envelope, or a library error.
 *
 * Paths are relative to the base URL and carry no leading slash:
 * `http.get('users')`, not `http.get('/users')`.
 */

const REQUEST_TIMEOUT_MS = 20_000

/**
 * ky hands the same Request object to `beforeRequest` and `afterResponse`, but
 * by then its body is consumed. Keep an untouched clone for the replay.
 */
const replayClones = new WeakMap<Request, Request>()

type SearchParams = Record<string, string | number | boolean | null | undefined>

type RequestOptions<TOut> = {
  searchParams?: SearchParams
  /** Request body, serialized as JSON. */
  json?: unknown
  /**
   * Parsed at the boundary. Exactly one domain type reaches the rest of the
   * app, whatever shape the wire uses.
   */
  schema?: z.ZodType<TOut>
  signal?: AbortSignal
  headers?: Record<string, string>
}

/** Drops absent values so `?status=undefined` never reaches the backend. */
function toSearchParams(params: SearchParams | undefined): Record<string, string> | undefined {
  if (params === undefined) return undefined

  const entries = Object.entries(params).flatMap(([key, value]) =>
    value === undefined || value === null || value === '' ? [] : [[key, String(value)] as const],
  )

  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

/**
 * The backend wraps every success in `{ success, data }`, and `data` is
 * omitted when it would be empty — a 2xx carrying nothing arrives as
 * `{"success":true}`. Unwrapping here means no schema in the app ever sees the
 * envelope, and a body without `success` passes through untouched.
 */
type Envelope = { success: boolean; data?: unknown }

const isEnvelope = (payload: unknown): payload is Envelope =>
  typeof payload === 'object' &&
  payload !== null &&
  'success' in payload &&
  typeof (payload as { success: unknown }).success === 'boolean'

const unwrapEnvelope = (payload: unknown): unknown =>
  isEnvelope(payload) ? (payload.data ?? null) : payload

function attachToken({ request }: BeforeRequestState) {
  const token = authSession.getToken()
  if (token !== null) request.headers.set('Authorization', `Bearer ${token}`)
  replayClones.set(request, request.clone())
}

/**
 * Refresh once — concurrent callers share that single refresh — then replay the
 * original request exactly once. On failure, reset everything and hand control
 * to the session-lost handler.
 *
 * `replayed` tells the caller whether the returned response is the retry or the
 * untouched original, which matters when the provider signals "stale credential"
 * with something other than a 401.
 */
async function refreshAndReplay(
  request: Request,
  response: Response,
): Promise<{ replayed: boolean; response: Response }> {
  const result = await authSession.refresh()
  if (!result.ok) {
    authSession.sessionLost()
    return { replayed: false, response }
  }

  const original = replayClones.get(request) ?? request
  const headers = new Headers(original.headers)
  if (result.token !== null) headers.set('Authorization', `Bearer ${result.token}`)

  // Plain fetch, not ky: the replay bypasses the hooks entirely, so a repeat of
  // the same status goes straight to the caller and a loop is impossible. No
  // marker header — a custom header would need its own CORS allowance in
  // production. ky's timeout does not cover this fetch, hence the explicit signal.
  const retried = await fetch(
    new Request(original, {
      headers,
      signal: AbortSignal.any([original.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]),
    }),
  )

  return { replayed: true, response: retried }
}

/**
 * A 403 is not a 401: the credential is valid and the action is forbidden, so
 * it falls straight through to the caller.
 */
async function handleUnauthorized({ request, response }: AfterResponseState) {
  if (response.status !== 401) return response

  return (await refreshAndReplay(request, response)).response
}

/** How a backend's success envelope is peeled off before a schema sees it. */
export type Unwrap = (payload: unknown) => unknown

type ClientOptions = {
  withAuth: boolean
  /** Defaults to the api-service `{ success, data }` envelope. */
  unwrap?: Unwrap
  /** Provider-specific response handling, run before the 401 flow. */
  afterResponse?: AfterResponseHook[]
  /**
   * Whether cookies ride along. `include` demands
   * `Access-Control-Allow-Credentials: true` back, and a cross-origin backend
   * that does not send it has its answer rejected by the browser whatever the
   * status was — which surfaces as a bare "CORS error" and nothing else.
   * So a bearer-only backend gets `omit`.
   */
  credentials?: RequestCredentials
}

function createClient(
  baseUrl: string,
  { withAuth, afterResponse = [], credentials = 'include' }: ClientOptions,
): KyInstance {
  return ky.create({
    // `prefix`, not `baseUrl`: it joins by path segment, so a base URL with a
    // path (`/api`) is never silently replaced by the request path.
    prefix: baseUrl,
    credentials,
    timeout: REQUEST_TIMEOUT_MS,
    // Reads retry on network trouble and 5xx. Mutations never retry, and no
    // 4xx is ever retried — a bad request stays bad.
    retry: {
      limit: 2,
      methods: ['get'],
      statusCodes: [408, 429, 500, 502, 503, 504],
    },
    hooks: {
      beforeRequest: withAuth ? [attachToken] : [],
      afterResponse: withAuth ? [...afterResponse, handleUnauthorized] : afterResponse,
    },
  })
}

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete'

async function request<TOut>(
  client: KyInstance,
  unwrap: Unwrap,
  method: Method,
  path: string,
  options: RequestOptions<TOut> = {},
): Promise<TOut> {
  const { searchParams, json, schema, signal, headers } = options

  try {
    const response = await client(path, {
      method,
      searchParams: toSearchParams(searchParams),
      json,
      signal,
      headers,
    })

    if (response.status === 204) return undefined as TOut

    // A 2xx with an empty body is "no content" whatever the status code says —
    // `response.json()` on it would throw a SyntaxError dressed as UNKNOWN.
    const text = await response.text()
    if (text === '') return undefined as TOut

    const payload = unwrap(JSON.parse(text))
    return schema === undefined ? (payload as TOut) : schema.parse(payload)
  } catch (error) {
    throw normalizeError(error)
  }
}

/** A file response and the name the server asked it be saved under. */
type BlobResult = { blob: Blob; filename: string }

type BlobOptions = {
  searchParams?: SearchParams
  signal?: AbortSignal
  /** Used when the server does not expose a readable `Content-Disposition`. */
  fallbackName: string
}

/** Pulls `filename` out of a `Content-Disposition`; null if the header is absent. */
function filenameFromDisposition(header: string | null): string | null {
  if (header === null) return null
  const value = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)?.[1]
  return value === undefined ? null : decodeURIComponent(value)
}

/**
 * A binary GET, kept apart from `request()` because that one reads the body as
 * text and hands it to `JSON.parse` — which would corrupt bytes and throw on a
 * file. It still rides the same client, so `attachToken` and the 401 refresh
 * apply. Reading the filename cross-origin needs the backend to list
 * `Content-Disposition` in `Access-Control-Expose-Headers`; when it does not,
 * `fallbackName` stands in.
 */
async function requestBlob(
  client: KyInstance,
  path: string,
  { searchParams, signal, fallbackName }: BlobOptions,
): Promise<BlobResult> {
  try {
    const response = await client(path, {
      method: 'get',
      searchParams: toSearchParams(searchParams),
      signal,
    })

    const filename = filenameFromDisposition(response.headers.get('content-disposition'))
    return { blob: await response.blob(), filename: filename ?? fallbackName }
  } catch (error) {
    throw normalizeError(error)
  }
}

export type Http = {
  get: <TOut>(path: string, options?: RequestOptions<TOut>) => Promise<TOut>
  post: <TOut>(path: string, options?: RequestOptions<TOut>) => Promise<TOut>
  put: <TOut>(path: string, options?: RequestOptions<TOut>) => Promise<TOut>
  patch: <TOut>(path: string, options?: RequestOptions<TOut>) => Promise<TOut>
  delete: <TOut>(path: string, options?: RequestOptions<TOut>) => Promise<TOut>
  /** A file download, carrying the bearer and the same refresh-and-replay as the rest. */
  getBlob: (path: string, options: BlobOptions) => Promise<BlobResult>
}

/**
 * Builds a facade over one backend. The envelope is a parameter because the
 * panel now talks to two of them, and they do not agree on the shape:
 * api-service wraps in `{ success, data }`, goauth in `{ data }`.
 */
export function createHttp(baseUrl: string, options: ClientOptions): Http {
  const client = createClient(baseUrl, options)
  const unwrap = options.unwrap ?? unwrapEnvelope

  return {
    get: (path, opts) => request(client, unwrap, 'get', path, opts),
    post: (path, opts) => request(client, unwrap, 'post', path, opts),
    put: (path, opts) => request(client, unwrap, 'put', path, opts),
    patch: (path, opts) => request(client, unwrap, 'patch', path, opts),
    delete: (path, opts) => request(client, unwrap, 'delete', path, opts),
    getBlob: (path, opts) => requestBlob(client, path, opts),
  }
}

/** The panel API. Every feature uses this. */
export const http = createHttp(env.VITE_API_URL, { withAuth: true })

/**
 * The realtime service answers on its own port. Its token endpoint sits behind
 * the same auth as everything else, so a 401 there deserves the same refresh
 * and replay.
 */
export const realtimeHttp = createHttp(env.VITE_REALTIME_API_URL, { withAuth: true })
